import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { MagnifyingGlass } from "@phosphor-icons/react";

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const search = useCallback((value) => {
    setQ(value);
    if (timer.current) clearTimeout(timer.current);
    if (value.trim().length < 2) { setGroups([]); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(value.trim())}`);
        setGroups(data.groups || []);
      } finally { setLoading(false); }
    }, 220);
  }, []);

  const go = (route) => { setOpen(false); setQ(""); setGroups([]); navigate(route); };

  return (
    <>
      <Button variant="outline" size="sm" data-testid="global-search-trigger"
        onClick={() => setOpen(true)} className="gap-2 text-muted-foreground">
        <MagnifyingGlass size={16} weight="bold" />
        <span className="hidden sm:inline">Suche</span>
        <kbd className="ml-1 hidden rounded border bg-muted px-1.5 text-[10px] font-semibold sm:inline">⌘K</kbd>
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
        <CommandInput data-testid="global-search-input" placeholder="Kunden, Anlagen, QR, Verträge, Aufträge, Dokumente …"
          value={q} onValueChange={search} />
        <CommandList>
          {loading && <div className="p-4 text-center text-sm text-muted-foreground">Suche …</div>}
          {!loading && q.trim().length >= 2 && groups.length === 0 && <CommandEmpty>Keine Treffer für „{q}".</CommandEmpty>}
          {!loading && q.trim().length < 2 && <div className="p-4 text-center text-sm text-muted-foreground">Mindestens 2 Zeichen eingeben.</div>}
          {groups.map((g) => (
            <CommandGroup key={g.label} heading={g.label}>
              {g.items.map((it) => (
                <CommandItem key={it.id} value={`${g.label}-${it.id}`} onSelect={() => go(it.route)}
                  data-testid={`search-result-${it.id}`} className="flex items-center justify-between gap-3">
                  <span className="truncate">{it.label}</span>
                  {it.sub && <span className="shrink-0 truncate text-xs text-muted-foreground">{it.sub}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
